/*
 * Module for dealing with Login.  This module also keeps a model of the User who has logged in
 * and provides reqres' for obtaining that data (default tp, context, interface language etc.)
 */

define([
  "marionette",
  "underscore",
  "modules/login/loginView",
  "modules/login/loginModel",
  "backbone.syphon",
  "messaging/messenger",
  "configuration/properties",
  "modules/accountAdmin/privilegeEnum",
  "modules/admin/userAdmin/passwordAdmin/passwordAdminModule",
  "jquery",
  "backbone",
  "backboneRetry",
], function (
  Marionette,
  _,
  LoginView,
  LoginModel,
  BackboneSyphon,
  Messenger,
  Properties,
  PrivilegeEnum,
  PasswordAdminModule,
  $,
  Backbone,
  BackboneRetry,
) {
  "use strict";

  const LOCAL_STORAGE_PREFIX = "astute_knowledge_";
  const ACCESS_TOKEN_KEY = LOCAL_STORAGE_PREFIX + "access_token";
  const REFRESH_TOKEN_KEY = LOCAL_STORAGE_PREFIX + "refresh_token";
  /**
   * Custom styling for various profiles so it is obvious to the user that they are not using the
   * production environment.
   */
  const STYLESHEET_PROFILES = {
    dev: `
:root {
  --account-admin-header-background-color: #0e7110;
  --active-admin-item-background-color: #2ccc00;
  --active-button-color: #2e812b;
  --astute-blue: #37a833;
  --astute-light-blue: #68c065;
  --menu-background-color: #87c17a;
  --selected-color: #39d317;
  --side-menu-admin-item-background-color: #589244;
}
`,
    local: `
:root {
  --account-admin-header-background-color: #71610e;
  --active-admin-item-background-color: #cca300;
  --active-button-color: #816d2b;
  --astute-blue: #a89533;
  --astute-light-blue: #c0ab65;
  --menu-background-color: #c1ab7a;
  --selected-color: #d39717;
  --side-menu-admin-item-background-color: #928044;
}
`,
    qa: `
:root {
  --account-admin-header-background-color: #710e0e;
  --active-admin-item-background-color: #cc0000;
  --active-button-color: #812b2b;
  --astute-blue: #a83333;
  --astute-light-blue: #c06565;
  --menu-background-color: #c17a7a;
  --selected-color: #d31717;
  --side-menu-admin-item-background-color: #924444;
}
`,
  };

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "login";
      this.region = options.region;
      this.login = new LoginModel.loginModel();
      this.user = new LoginModel.userModel();
      this.loggingIn = false; //flag to prevent multiple log in attempts at once
      this.sessionReauthenticationTimeout = false;
      this.tokenReauthenticateTimeout = false;
      this._setUpReqres();
      this._setUpCommands();
      this._setUpVents();
      this._setUpAuth();
    },
    show: function () {
      this.login.showLoginForm = Properties.loginMethod != "redirect";
      this.view = new LoginView.loginView({ model: this.login });
      this.view.on("login", (args) => {
        if (!this.loggingIn) {
          //prevent multiple login attempts
          this._beginLogin();
          const data = BackboneSyphon.serialize(args.view);
          this.login.set(data);
          this._validateLogin();
        }
      });
      this.showRegion(this.view);
    },
    getCsrfToken: function () {
      let xsrfTokenValue;
      if (this.authModel) {
        xsrfTokenValue = this.authModel.get("csrfToken");
        if (xsrfTokenValue) {
          return xsrfTokenValue;
        }
      }
      if (this.user) {
        xsrfTokenValue = this.user.get("csrfToken");
        if (xsrfTokenValue) {
          return xsrfTokenValue;
        }
      }
      const allCookies = document.cookie;
      if (allCookies) {
        const cookieParts = allCookies.split(/\s*;\s*/);
        const xsrfTokenCookie = cookieParts.find((cookiePart) =>
          cookiePart.startsWith("XSRF-TOKEN="),
        );
        if (xsrfTokenCookie) {
          xsrfTokenValue = xsrfTokenCookie.split("=")[1].trim();
          if (xsrfTokenValue) {
            return xsrfTokenValue;
          }
        }
      }
      return null;
    },
    loginIfNeeded: function () {
      if (this.login && this.login.get("error")) {
        this.show();
      } else if (this.refreshToken) {
        this._oauthSuccess();
      } else if (this.useSessionCookie) {
        // Everything needed to happen occurs when the auth model finishes loading
      } else if (this.authModel && this.authModel.checkFetching()) {
        // Wait for it to finish
      } else {
        this._needLogin();
      }
    },
    _beginLogin: function () {
      this.loggingIn = true;
      if (this.view) {
        this.view.beginLogin();
      }
    },
    _endLogin: function () {
      this.loggingIn = false;
      if (this.view) {
        this.view.endLogin();
      }
    },
    _setUser: function (attributes) {
      this.user.set(attributes, { silent: true }); //silence to keep the model clean
      // noinspection JSUnresolvedVariable
      if (attributes.deployVersion) {
        Properties.version = attributes.deployVersion;
      }
      if (attributes.activeProfiles && attributes.activeProfiles.length) {
        _.each(attributes.activeProfiles, (profile) => {
          const stylesheet = STYLESHEET_PROFILES[profile];
          if (stylesheet) {
            const stylesheetEl = document.createElement("style");
            stylesheetEl.setAttribute("type", "text/css");
            stylesheetEl.innerHTML = stylesheet;
            document.head.appendChild(stylesheetEl);
          }
        });
      }
      if (attributes.mixpanelToken) {
        Messenger.vent.trigger("mixpanel:init", attributes);
      }
    },
    _validateLogin: function () {
      const username = this.login.get("loginName");
      const password = this.login.get("password");
      //check for valid input before making the REST call
      if (username === "") {
        this.login.set("errorText", "login.errorNoUserName");
        this.login.set("error", true);
        this._endLogin();
        return;
      }
      if (password === "") {
        this.login.set("errorText", "login.errorNoPassword");
        this.login.set("error", true);
        this._endLogin();
        return;
      }
      //get a jwt token.  This will also validate that the username and password are correct.
      const oauthModel = new LoginModel.oauthModel({
        grant_type: "password",
        username: username,
        password: password,
      });
      oauthModel.save(null, {
        success: () => {
          this.accessToken = oauthModel.get("access_token");
          this.refreshToken = oauthModel.get("refresh_token");
          this.tokenExpiresInSeconds = oauthModel.get("expires_in");
          this._oauthSuccess();
        },
        error: () => {
          this.login.set("errorText", "login.errorInvalidLogin");
          this.login.set("error", true);
          //reset password
          this.login.set("password", "");
          this._endLogin();
        },
      });
    },
    _getKbDefaults: function (kbId) {
      const currentKbId = this._getOrDefaultKbId(kbId);
      const kbDefaults = this.user.get("kbDefaults");
      for (let i = 0; i < kbDefaults.length; i++) {
        if (kbDefaults[i].id === currentKbId) {
          return kbDefaults[i];
        }
      }
      return null;
    },
    _getOrDefaultKbId: function (kbId) {
      let currentKbId = 0;
      if (kbId) {
        if (typeof kbId === "number") {
          currentKbId = kbId;
        } else if (typeof kbId === "string") {
          currentKbId = parseInt(kbId, 10);
        } else {
          currentKbId = Number(kbId);
        }
      } else {
        try {
          currentKbId = Number(Messenger.reqres.request("active:kb"));
        } catch (ex) {
          //Oops.  There is no active KB yet.  We shall fall back to the defaults below.
          //This will typically occur when the menu bar is trying to figure out if the user is an author, at login
        }
      }
      //If we weren't supplied one, and there isn't an active one, go with the user's default.  Probably is a regular
      // login.
      if (!currentKbId) {
        currentKbId = this.user.get("tenantDefaults").defaultKbId;
      }
      return currentKbId;
    },
    _getKbDefaultProperty: function (propertyName) {
      //get the property for the current KB and convert it from a String to an "int"
      const kbId = parseInt(Messenger.reqres.request("active:kb"), 10);
      const kbDefaults = _.findWhere(this.user.get("kbDefaults"), { id: kbId });
      return kbDefaults.value[propertyName];
    },
    _hasKbRolePrivilege: function (kbId, privilegeId, foreignKey) {
      if (this._isAdmin()) {
        return true;
      }
      if (!this.kbRolePrivilegeMap) {
        this.kbRolePrivilegeMap = this.user.get("kbRolePrivileges");
      }
      const currentKbId = this._getOrDefaultKbId(kbId);
      let kbRolePrivilegeMap;
      if (this.kbRolePrivilegeMap) {
        kbRolePrivilegeMap = this.kbRolePrivilegeMap[currentKbId];
      }
      // Default if there are no KB role privileges is that the privilege is allowed if the user is an Author
      if (!kbRolePrivilegeMap) {
        return this._isAuthor(kbId);
      }
      const kbRolePrivilegeArray = kbRolePrivilegeMap[privilegeId];
      if (!kbRolePrivilegeArray || !kbRolePrivilegeArray.length) {
        return foreignKey && _.isEmpty(kbRolePrivilegeMap);
      }
      if (!foreignKey) {
        return true;
      }
      return _.some(kbRolePrivilegeArray, function (kbRolePrivilege) {
        // Purposely using == so that a string will automatically be converted to a number if needed
        return kbRolePrivilege.foreignKey == foreignKey;
      });
    },
    _hasTouchPointPrivilege: function (touchPointId) {
      if (this._isAdmin()) {
        return true;
      }
      const touchPoints = Messenger.reqres.request("kb:touchpoints");
      const touchPoint = touchPoints.get(touchPointId);
      return !!touchPoint;
    },
    _isAdmin: function () {
      return this.user.get("admin") || this._isGlobalAdmin();
    },
    _isAuthor: function (kbId) {
      if (this._isAdmin()) {
        return true;
      }
      const currentKb = this._getKbDefaults(kbId);
      if (!currentKb) {
        return false;
      }
      return !currentKb.value.reportsOnly && !currentKb.value.reviewsOnly;
    },
    _isGlobalAdmin: function () {
      return this.user.get("globalAdmin");
    },
    _isUsingKbRolePrivileges: function (kbId) {
      if (!this.kbRolePrivilegeMap) {
        this.kbRolePrivilegeMap = this.user.get("kbRolePrivileges");
      }
      const currentKbId = this._getOrDefaultKbId(kbId);
      let kbRolePrivilegeMap;
      if (this.kbRolePrivilegeMap) {
        kbRolePrivilegeMap = this.kbRolePrivilegeMap[currentKbId];
      }
      return kbRolePrivilegeMap;
    },
    _setUpReqres: function () {
      //get the user model
      Messenger.reqres.setHandler(
        "user",
        function () {
          return this.user;
        },
        this,
      );

      Messenger.reqres.setHandler("user:is:global:admin", this._isGlobalAdmin, this);
      Messenger.reqres.setHandler("user:is:admin", this._isAdmin, this);

      /**
       * Revamped on 9/25/2014.  It now optionally takes a kbId parameter; if
       * present, it will use that KB id.  When known or easily accessible, it
       * is preferable that this is provided.
       * If not given, it will fall back to the active kb, which generally will
       * be available.
       * If that is not available (at this point, only for the initial load of
       * the menu bar module, which reloads when the KBs sync), it uses the user's
       * default.
       */
      Messenger.reqres.setHandler("user:is:author", this._isAuthor, this);

      Messenger.reqres.setHandler(
        "user:is:reviewer",
        function (kbId) {
          const currentKb = this._getKbDefaults(kbId);
          if (!currentKb) {
            return false;
          }
          return currentKb.value.reviewsOnly;
        },
        this,
      );

      Messenger.reqres.setHandler(
        "user:is:reviewerWithAttributes",
        function (kbId) {
          const currentKb = this._getKbDefaults(kbId);
          if (!currentKb) {
            return false;
          }
          return currentKb.value.reviewsOnlyWithAttributes;
        },
        this,
      );

      Messenger.reqres.setHandler(
        "user:is:reporter",
        function (kbId) {
          const currentKb = this._getKbDefaults(kbId);
          if (!currentKb) {
            return false;
          }
          return currentKb.value.reportsOnly;
        },
        this,
      );

      Messenger.reqres.setHandler(
        "user:is:adhoc:reporter",
        function (kbId) {
          const currentKb = this._getKbDefaults(kbId);
          if (!currentKb) {
            return false;
          }
          return currentKb.value.adHocReports;
        },
        this,
      );

      Messenger.reqres.setHandler(
        "user:is:adhoc:admin",
        function (kbId) {
          const currentKb = this._getKbDefaults(kbId);
          if (!currentKb) {
            return false;
          }
          return currentKb.value.adHocAdmin;
        },
        this,
      );
      //get default kb id
      Messenger.reqres.setHandler(
        "default:kbId",
        function () {
          return this.user.get("tenantDefaults").defaultKbId;
        },
        this,
      );
      //get username
      Messenger.reqres.setHandler(
        "username",
        function () {
          return this.user.get("userName");
        },
        this,
      );
      //get default touchPoint
      Messenger.reqres.setHandler(
        "default:touchpoint",
        function () {
          return this._getKbDefaultProperty("touchPointId");
        },
        this,
      );
      //get default (response) language
      Messenger.reqres.setHandler(
        "default:response:language",
        function () {
          return this._getKbDefaultProperty("responseLanguageId");
        },
        this,
      );
      //get default (interface) language
      Messenger.reqres.setHandler(
        "default:interface:language",
        function () {
          return this.user.get("tenantDefaults").interfaceLanguageId;
        },
        this,
      );
      //get default (interface) dateformat
      Messenger.reqres.setHandler(
        "default:interface:dateFormat",
        function () {
          return this.user.get("tenantDefaults").dateFormat;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "default:showArchivedTopics",
        function () {
          return this.user.get("tenantDefaults").showArchivedTopics;
        },
        this,
      );
      //get default status id
      Messenger.reqres.setHandler(
        "default:topic:status",
        function () {
          return this._getKbDefaultProperty("statusId");
        },
        this,
      );
      //get default context id
      Messenger.reqres.setHandler(
        "default:topic:context",
        function () {
          return this._getKbDefaultProperty("contextId");
        },
        this,
      );
      //get the minimum size for emerging topics for it to trigger the dashboard
      Messenger.reqres.setHandler(
        "emerging:topic:threshold",
        function () {
          return Properties.emergingTopicsThreshold;
        },
        this,
      );
      //get the quick create UI configuration.  If true, the home button should send us to the quick create UI
      //and the UI should appear when we log in with an empty hash
      Messenger.reqres.setHandler(
        "default:quick:create",
        function () {
          return this._getKbDefaultProperty("showQuickCreate");
        },
        this,
      );
      //Returns boolean for whether the user has permission to view a certain kbId
      Messenger.reqres.setHandler(
        "can:view:kb",
        function (kbId) {
          const kbs = this.user.get("kbDefaults");
          for (let i = 0; i < kbs.length; i++) {
            if (kbs[i].id == kbId) {
              return true;
            }
          }
          return false;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:view:contentTab",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.ContentTab);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:view:learnerTab",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.LearnerTab);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:view:substitutionsTab",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.SubstitutionsTab);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:delete:topicAttributes",
        function (kbId) {
          return (
            this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicDeleteCategoryCodes) ||
            Messenger.reqres.request("user:is:reviewerWithAttributes")
          );
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:topicAttributes",
        function (kbId) {
          return (
            this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicEditCategoryCodes) ||
            Messenger.reqres.request("user:is:reviewerWithAttributes")
          );
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:view:topicAttributes",
        function (kbId) {
          return (
            this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicViewCategoryCodes) ||
            Messenger.reqres.request("user:is:reviewer")
          );
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:delete:topicCategoryCodes",
        function (kbId) {
          return (
            this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicDeleteCategoryCodes) ||
            Messenger.reqres.request("user:is:reviewerWithAttributes")
          );
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:topicCategoryCodes",
        function (kbId) {
          return (
            this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicEditCategoryCodes) ||
            Messenger.reqres.request("user:is:reviewerWithAttributes")
          );
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:view:topicCategoryCodes",
        function (kbId) {
          return (
            this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicViewCategoryCodes) ||
            Messenger.reqres.request("user:is:reviewer")
          );
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:delete:topicExamples",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicDeleteExamples);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:topicExamples",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicEditExamples);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:view:topicExamples",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicViewExamples);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:topicFolder",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicEditFolder);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:topicLabels",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicEditLabels);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:view:topicLabels",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicViewLabels);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:delete:topic",
        function (kbId, isTopicArchived) {
          if (isTopicArchived) {
            return this._isAdmin();
          } else {
            return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicDelete);
          }
        },
        this,
      );
      Messenger.reqres.setHandler("can:archive:topic", this._isAdmin, this);
      Messenger.reqres.setHandler(
        "can:edit:topicContext",
        (kbId) =>
          // NOTE: There is currently no permission for changing the Topic Context.
          // This feels like an oversight, but for now base it on being able to edit the Topic Name.
          this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicEditName),
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:topicName",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicEditName);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:topicStatus",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicEditStatus);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:create:topic",
        function (kbId) {
          return this._hasKbRolePrivilege(kbId, PrivilegeEnum.TopicEditName);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:delete:locale",
        function (options) {
          options = options || {};
          if (this._isUsingKbRolePrivileges(options.kbId)) {
            return this._hasKbRolePrivilege(
              options.kbId,
              PrivilegeEnum.LocaleDeleteSpecific,
              options.languageId,
            );
          }
          return true;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:locale",
        function (options) {
          options = options || {};
          if (this._isUsingKbRolePrivileges(options.kbId)) {
            return this._hasKbRolePrivilege(
              options.kbId,
              PrivilegeEnum.LocaleEditSpecific,
              options.languageId,
            );
          }
          return true;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:view:locale",
        function (options) {
          options = options || {};
          if (this._isUsingKbRolePrivileges(options.kbId)) {
            return this._hasKbRolePrivilege(
              options.kbId,
              PrivilegeEnum.LocaleViewSpecific,
              options.languageId,
            );
          }
          return true;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:delete:touchPoint",
        function (options) {
          options = options || {};
          if (this._isUsingKbRolePrivileges(options.kbId)) {
            return this._hasKbRolePrivilege(
              options.kbId,
              PrivilegeEnum.TouchpointDeleteSpecific,
              options.touchPointId,
            );
          }
          return this._hasTouchPointPrivilege(options.touchPointId);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:touchPoint",
        function (options) {
          options = options || {};
          if (this._isUsingKbRolePrivileges(options.kbId)) {
            return this._hasKbRolePrivilege(
              options.kbId,
              PrivilegeEnum.TouchpointEditSpecific,
              options.touchPointId,
            );
          }
          return this._hasTouchPointPrivilege(options.touchPointId);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:view:touchPoint",
        function (options) {
          options = options || {};
          if (this._isUsingKbRolePrivileges(options.kbId)) {
            return this._hasKbRolePrivilege(
              options.kbId,
              PrivilegeEnum.TouchpointViewSpecific,
              options.touchPointId,
            );
          }
          return this._hasTouchPointPrivilege(options.touchPointId);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:create:task",
        function (options) {
          options = options || {};
          return this._hasKbRolePrivilege(options.kbId, PrivilegeEnum.TaskCreate);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:edit:any:task",
        function (options) {
          options = options || {};
          return this._hasKbRolePrivilege(options.kbId, PrivilegeEnum.AnyTaskEdit);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:review:response",
        function (options) {
          options = options || {};
          return this._hasKbRolePrivilege(options.kbId, PrivilegeEnum.ResponseReview);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "can:delete:customTemplate",
        function (options) {
          options = options || {};
          return this._hasKbRolePrivilege(options.kbId, PrivilegeEnum.ContentTab);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "authorization:header",
        function () {
          if (this.accessToken) {
            return "Bearer " + this.accessToken;
          }
          return "";
        },
        this,
      );
      Messenger.reqres.setHandler(
        "get:download:url",
        function (uploadId, fileName) {
          const downloadServerUrl = this.user.get("downloadServerUrl");
          if (!uploadId) {
            return downloadServerUrl;
          }
          const kbId = Messenger.reqres.request("active:kb");
          return (
            downloadServerUrl +
            kbId +
            "/" +
            encodeURIComponent(uploadId) +
            "/" +
            encodeURIComponent(fileName)
          );
        },
        this,
      );
      Messenger.reqres.setHandler(
        "get:download:cookie",
        function () {
          // This is a bit of a hack to allow a loading view to be shown.  There is really never anything
          // stored in this model, it just exists to pretend to be a model that is loading from the server.
          const downloadCookie = new Backbone.Model();
          if (this.accessToken && this.user.get("downloadServerUrl")) {
            downloadCookie.trigger("request");
            fetch(this.user.get("downloadServerUrl"), {
              method: "POST",
              mode: "cors",
              credentials: "include",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: "access_token=" + encodeURIComponent(this.accessToken),
            })
              .catch((reason) => console.error("Failed to get Download Server cookie:", reason))
              .then((response) => {
                if (!response.ok) {
                  console.error("Failed to get Download Server cookie:", response);
                }
              })
              .finally(() => {
                downloadCookie.trigger("fetched");
                downloadCookie.trigger("sync");
              });
          } else {
            downloadCookie.trigger("fetched");
            downloadCookie.trigger("sync");
          }
          return downloadCookie;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "get:buildDate",
        function () {
          const deployBuildDate = this.user.get("deployBuildDate");
          if (deployBuildDate) {
            return Messenger.reqres.request("get:formatted:date:long", deployBuildDate);
          }
          return "";
        },
        this,
      );
      Messenger.reqres.setHandler(
        "get:version",
        function () {
          return this.user.get("deployVersion") || "";
        },
        this,
      );
      Messenger.reqres.setHandler("get:csrfToken", () => this.getCsrfToken());
      Messenger.reqres.setHandler("get:uploadUrl", () => {
        let uploadUrl = Properties.uploadUrl;
        const csrfToken = this.getCsrfToken();
        if (csrfToken) {
          uploadUrl += "?_csrf=" + encodeURIComponent(csrfToken);
        }
        return uploadUrl;
      });
      Messenger.reqres.setHandler(
        "get:ckeditor:bundleVersion",
        () => this.user.get("ckeditorBundleVersion") || "",
      );
      Messenger.reqres.setHandler(
        "get:ckeditor:licenseKey",
        () => this.user.get("ckeditorLicenseKey") || "",
      );
      Messenger.reqres.setHandler(
        "get:ckeditor:tokenUrl",
        () => this.user.get("ckeditorTokenUrl") || "",
      );
      Messenger.reqres.setHandler(
        "get:ckeditor:webSocketUrl",
        () => this.user.get("ckeditorWebSocketUrl") || "",
      );
      Messenger.reqres.setHandler(
        "get:wproofreader:serviceId",
        () => this.user.get("wproofreaderServiceId") || "",
      );
      Messenger.reqres.setHandler(
        "get:wproofreader:srcUrl",
        () => this.user.get("wproofreaderSrcUrl") || "",
      );
      Messenger.reqres.setHandler("topic:read:only", () => {
        return !Messenger.reqres.request("user:is:author");
      });
    },
    _setUpCommands: function () {
      Messenger.command.setHandler(
        "add:user:kb:defaults",
        function (kb) {
          const kbs = this.user.get("kbDefaults");
          const newKbDefaults = {
            id: kb.get("kbId"),
            value: {
              contextId: kb.getContexts().first().get("id"),
              reportsOnly: false,
              responseLanguageId: 1,
              reviewsOnly: false,
              reviewsOnlyWithAttributes: false,
              adHocReports: false,
              adHocAdmin: false,
              showQuickCreate: true,
              statusId: 9,
              touchPointId: kb.getTouchPoints().first().get("touchPointId"),
            },
          };
          kbs.push(newKbDefaults);
          this.user.set("kbDefaults", kbs);
        },
        this,
      );
    },
    _showChangePasswordModal: function () {
      const defaultKbId = Messenger.reqres.request("default:kbId");
      const kbs = Messenger.reqres.request("kbs");
      kbs.onSynced(function () {
        const kb = kbs.get(defaultKbId);
        const tenantId = kb.get("tenantId");
        const tenant = Messenger.reqres.request("fetch:tenant", tenantId);
        Messenger.vent.trigger(
          "show:modal",
          PasswordAdminModule,
          {
            preventCancel: true,
            tenant: tenant,
          },
          {},
          {
            title: "user.expiredPassword",
            closeButton: false,
            closeOnBackgroundClick: false,
            modalWidth: 500,
            trackInteraction: {
              action: "Change Expired Password",
              object: "User Password",
            },
          },
        );
      }, this);
    },
    _clearOAuth: function () {
      BackboneRetry.set({
        reauthenticate: null,
      });
      sessionStorage.setItem(ACCESS_TOKEN_KEY, "");
      sessionStorage.setItem(REFRESH_TOKEN_KEY, "");
      this.accessToken = "";
      this.refreshToken = "";
    },
    _finishLogout: function () {
      if (Properties.loginMethod == "redirect") {
        //redirect to logout
        location.replace(`${Properties.adminRootURL}/logout`);
      } else {
        //redirect to home
        Backbone.history.navigate("");
        //refresh page
        location.reload();
      }
    },
    _oauthSuccess: function () {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, this.accessToken);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, this.refreshToken);
      BackboneRetry.set({
        reauthenticate: this._reauthenticateToken.bind(this),
      });
      this._authSuccess();
    },
    _authSuccess: function () {
      if (this.login.isRequested || this.login.isFetched) {
        return;
      }
      // Load information about the logged-in user
      this.login.fetch({
        success: (model) => {
          //strip password and error field attributes from the model
          model.unset("password", { silent: true });
          model.unset("error", { silent: true });
          model.unset("errorText", { silent: true });
          //store user data
          this._setUser(model.attributes);
          Messenger.command.execute("set:interface:language");
          //login is complete
          Messenger.vent.trigger("login:complete");
          this._endLogin();
          if (model.get("passwordExpired")) {
            this._showChangePasswordModal();
          }
          if (this.tokenExpiresInSeconds > 0) {
            this.tokenReauthenticateTimeout = setTimeout(
              this._reauthenticateToken.bind(this),
              this.tokenExpiresInSeconds * 1000,
            );
          }
        },
        error: (model, response) => {
          if (response.status == 400) {
            let newErrorText = "login.errorNoKb";
            //bad request
            if (response.responseJSON) {
              let responseValue;
              if (Array.isArray(response.responseJSON.value)) {
                responseValue = response.responseJSON.value[0];
              } else {
                responseValue = response.responseJSON.value;
              }
              if (responseValue === "User is not permitted to view any touchpoints") {
                newErrorText = "login.errorNoTouchpoints";
              } else if (responseValue === "User is not permitted to view any languages") {
                newErrorText = "login.errorNoLanguages";
              }
            }
            model.set("errorText", newErrorText);
          } else if (response.status == 412) {
            model.set("errorText", "login.errorAccountLocked");
          } else {
            model.set("errorText", "login.errorInvalidLogin");
            this._clearOAuth();
          }
          model.set("error", true);
          //reset password
          model.set("password", "");
          this._endLogin();
        },
      });
    },
    _reauthenticateSession: function () {
      if (this.sessionReauthenticationTimeout) {
        clearTimeout(this.sessionReauthenticationTimeout);
        this.sessionReauthenticationTimeout = false;
      }
      if (!this.useSessionCookie) {
        return;
      }
      if (!this.authModel) {
        this.authModel = new LoginModel.authModel();
      }
      this.authModel.fetch({
        error: () => (this.useSessionCookie = false),
        success: (model) => this._sessionAuthSuccess(model),
      });
    },
    _reauthenticateToken: function () {
      const refreshDeferred = $.Deferred();
      if (this.tokenReauthenticateTimeout) {
        clearTimeout(this.tokenReauthenticateTimeout);
        this.tokenReauthenticateTimeout = false;
      }
      if (!this.refreshToken) {
        refreshDeferred.reject();
      } else {
        const refreshModel = new LoginModel.oauthModel({
          grant_type: "refresh_token",
          refresh_token: this.refreshToken,
        });
        this._clearOAuth();
        refreshModel.save(null, {
          success: (model, response, options) => {
            this.accessToken = model.get("access_token");
            this.refreshToken = model.get("refresh_token");
            this.tokenExpiresInSeconds = model.get("expires_in");
            if (this.tokenExpiresInSeconds > 0) {
              this.tokenReauthenticateTimeout = setTimeout(
                this._reauthenticateToken.bind(this),
                this.tokenExpiresInSeconds * 1000,
              );
            }
            sessionStorage.setItem(ACCESS_TOKEN_KEY, this.accessToken);
            sessionStorage.setItem(REFRESH_TOKEN_KEY, this.refreshToken);
            BackboneRetry.set({
              reauthenticate: this._reauthenticateToken.bind(this),
            });
            refreshDeferred.resolve(model, response, options);
          },
          error: (model, response, options) => {
            refreshDeferred.reject(model, response, options);
          },
        });
      }
      return refreshDeferred.promise();
    },
    _needLogin: function () {
      if (Properties.loginMethod == "redirect") {
        this._redirectToAuth();
      } else {
        this.show();
      }
    },
    _redirectToAuth: function () {
      this.loggingIn = true;
      window.document.location.replace(
        `${Properties.adminRootURL}/auth?nextUrl=${encodeURIComponent(
          window.document.location.href,
        )}`,
      );
    },
    _sessionAuthSuccess: function (authModel) {
      const timeoutMilliseconds = authModel.get("timeoutMilliseconds");
      // Allow a session timeout to be pulled from the response, but favor requesting every five minutes if the
      // timeout is greater than that.  But, also do not let the timeout go below 1 minute.
      const fiveMinutes = 5 * 60 * 1000;
      const oneMinute = 60 * 1000;
      const sessionRepeatMilliseconds =
        timeoutMilliseconds >= fiveMinutes
          ? fiveMinutes
          : timeoutMilliseconds <= oneMinute
            ? oneMinute
            : timeoutMilliseconds;
      this.useSessionCookie = true;
      this.sessionReauthenticationTimeout = setTimeout(
        () => this._reauthenticateSession(),
        sessionRepeatMilliseconds,
      );
    },
    _setUpAuth: function () {
      const hash = document.location.hash;
      this.accessToken = null;
      this.refreshToken = null;
      this.useSessionCookie = false;
      if (hash && hash.indexOf(";") > 0) {
        try {
          const pos = hash.indexOf(";");
          const tokenParts = hash.substring(pos + 1).split("&");
          document.location.hash = hash.substring(0, pos);
          for (const tokenPart of tokenParts) {
            const equalPos = tokenPart.indexOf("=");
            if (equalPos > 0) {
              const key = tokenPart.substring(0, equalPos);
              const value = tokenPart.substring(equalPos + 1);
              switch (key) {
                case "access_token":
                  this.accessToken = value;
                  break;
                case "refresh_token":
                  this.refreshToken = value;
                  break;
              }
            }
          }
        } catch (e) {
          console.error("Failed to decode access token and refresh token", e);
        }
      } else {
        this.accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
        this.refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
      }
      const ajaxSetupOptions = {
        beforeSend: (xhr) => {
          if (this.accessToken) {
            xhr.setRequestHeader("Authorization", "Bearer " + this.accessToken);
          }
          const csrfToken = this.getCsrfToken();
          if (csrfToken) {
            // Set the X-XSRF-TOKEN header so the request works properly with Spring Security CSRF
            // protection.
            xhr.setRequestHeader("X-XSRF-TOKEN", csrfToken);
          }
        },
      };
      if (Properties.allowCors) {
        ajaxSetupOptions.xhrFields = {
          withCredentials: true,
        };
      }
      // noinspection JSUnusedGlobalSymbols
      $.ajaxSetup(ajaxSetupOptions);
      // This must come after setting up AJAX for jQuery, otherwise the call to the authentication
      // end-point will be missing the cookies if the call is cross-domain
      if (!this.accessToken && !this.refreshToken) {
        this._trySessionAuth();
      }
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "logout",
        function () {
          const refreshToken = this.refreshToken;
          this._clearOAuth();
          if (this.tokenReauthenticateTimeout) {
            clearTimeout(this.tokenReauthenticateTimeout);
            this.tokenReauthenticateTimeout = false;
          }
          if (refreshToken) {
            const revokeModel = new LoginModel.revokeOauthModel({
              refresh_token: refreshToken,
            });
            revokeModel.save(null, {
              success: this._finishLogout.bind(this),
              error: this._finishLogout.bind(this),
            });
          } else {
            this._finishLogout();
          }
        },
        this,
      );
      Messenger.vent.on(
        "app:close",
        function (status) {
          status.login = this.loggingIn;
        },
        this,
      );
    },
    _trySessionAuth: function () {
      this.authModel = new LoginModel.authModel();
      this.authModel.fetch({
        error: () => this._needLogin(),
        success: (model) => {
          this._sessionAuthSuccess(model);
          this._authSuccess();
        },
      });
    },
  });
});
